import { FormProvider, useForm } from 'react-hook-form'
import React, { useCallback, useContext, useState } from 'react'
import { StandardRegisterProps, State } from '.'
import {
  validationSchemaAccount,
  validationSchemaCompanyInformation,
  validationSchemaEmail,
} from './validation'
import { useGraphqlMutation } from '@dustin-web/microsite-data/src/hooks'
import {
  MergeCartsById,
  MergeCartsByIdMutation,
  MergeCartsByIdMutationVariables,
  RegisterBusiness,
  RegisterBusinessMutation,
  RegisterBusinessMutationVariables,
  RegisterCustomerStatus,
} from 'src/graphql/generated/graphql-types'
import Error from './Error'
import DustinLogoSpinner from '@dustin-web/microsite-components/src/spinner'
import StepperProcess from '@dustin-web/microsite-components/src/stepper-process'
import { useResponsive, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import cookieHandler from '@dustin-web/auth-js/dist/handlers/cookieHandler'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import CustomRedirect from 'src/components/customRedirect'
import { accountCreated } from 'src/util/googleAnalyticsWrapperV2'
import AppContext from 'src/appContext'
import ValidationMessage from 'src/components/validationMessage'

const useYupValidationResolver = (state: State) =>
  useCallback(
    async (data: StandardRegisterProps) => {
      try {
        const schema = schemas[State[state]]

        const values = await schema.validate(data, {
          abortEarly: false,
        })

        return {
          values,
          errors: {},
        }
      } catch (errors: any) {
        return {
          values: {},
          errors: errors.inner.reduce(
            (allErrors: any, currentError: any) => ({
              ...allErrors,
              [currentError.path]: {
                type: currentError.type ?? 'validation',
                message: currentError.message,
              },
            }),
            {}
          ),
        }
      }
    },
    [state]
  )

const schemas: any = {
  email: validationSchemaEmail,
  companyInfo: validationSchemaCompanyInformation,
  account: validationSchemaAccount,
}

type FormProps = {
  defaultValues: StandardRegisterProps
  state: State
  returnUrl: string | null
  onStateChange: (state: State) => void
  children: React.ReactNode
}

const Form: React.FC<FormProps> = ({
  defaultValues,
  state,
  returnUrl,
  onStateChange,
  children,
}) => {
  const [errorRegisteringContact, setErrorRegisteringContact] = useState<boolean>(false)
  const { setCurrentContact } = useAuth()
  const { sourcingCompany } = useContext(AppContext)
  const { translate } = useTranslations()
  const cookies = cookieHandler()
  const [status, setStatus] = useState<RegisterCustomerStatus | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [redirectLink, setRedirectLink] = useState<string | null>(null)
  const { isSmallScreen } = useResponsive()

  const methods = useForm<StandardRegisterProps>({
    resolver: useYupValidationResolver(state),
    defaultValues,
  })

  const [mergeCartsById] = useGraphqlMutation<
    MergeCartsByIdMutation,
    MergeCartsByIdMutationVariables
  >(MergeCartsById)

  const [mutation] = useGraphqlMutation<
    RegisterBusinessMutation,
    RegisterBusinessMutationVariables
  >(RegisterBusiness)

  const checkKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.code === 'Enter') e.preventDefault()
  }

  const onSubmit = async ({
    acceptAccountTerms,
    companyName,
    email,
    firstName,
    lastName,
    offersByEmail,
    organizationNumber,
    password,
    preRegistrationToken,
  }: StandardRegisterProps) => {
    if (state !== State.account) {
      onStateChange(state + 1)
    } else {
      setErrorRegisteringContact(false)
      setLoading(true)

      const result: RegisterBusinessMutation = await mutation({
        variables: {
          accountTermsAccepted: acceptAccountTerms,
          companyName,
          firstName,
          lastName,
          offersByEmail,
          email: email == '' ? defaultValues.email : email,
          orgNumber: organizationNumber,
          password,
          preRegistrationToken,
        },
      })

      if (!result) {
        setErrorRegisteringContact(true)
      }

      if (
        result?.registerBusiness?.status === RegisterCustomerStatus.Success &&
        result?.registerBusiness?.contactId
      ) {
        const { cartId, isAuthenticated } = defaultValues

        accountCreated(sourcingCompany)

        if (isAuthenticated) {
          try {
            if (!!cartId) {
              await mergeCartsById({
                variables: {
                  sourceCartId: cartId,
                  targetContactId: result.registerBusiness.contactId,
                },
              })
            }
          } catch (error) {
            console.error('Failed to merge cart', {
              error,
              contactId: result.registerBusiness.contactId,
            })
          }

          await setCurrentContact(result.registerBusiness.contactId)
          setRedirectLink(returnUrl ?? '/')
        } else {
          cookies.setContactId(result.registerBusiness.contactId)
          onStateChange(State.confirmation)
        }
      }

      setStatus(result?.registerBusiness?.status ?? null)
      setLoading(false)
    }
  }

  if (redirectLink) {
    return <CustomRedirect url={redirectLink} />
  }

  return (
    <FormProvider {...methods}>
      {loading && <DustinLogoSpinner dustinSpinner />}

      {!isSmallScreen && (
        <StepperProcess
          labels={[
            translate('Register_email'),
            translate('REGISTRATION_COMPANY_INFORMATION'),
            translate('RegisterBuinsess_Account_Information'),
          ]}
          currentIdx={state}
          bgCompletedClassName={'u-bg-dark-gray'}
          activeStepClassName="u-color-dark-gray u-fw-bold"
        />
      )}

      <div className="u-pt-12 u-pt-lg-64">
        <form onSubmit={methods.handleSubmit(onSubmit)} onKeyDown={checkKeyDown}>
          {children}
        </form>
      </div>

      {errorRegisteringContact && (
        <div className="row">
          <div className="col-12">
            <ValidationMessage>{translate('REGISTRATION_UNKNOWN_ERROR')}</ValidationMessage>
          </div>
        </div>
      )}

      <Error email={methods.getValues('email')} returnUrl={returnUrl} status={status} />
    </FormProvider>
  )
}

export default Form
